// src/pages/ResidencesPage.js
import React from 'react';
import stagedLivingKitchen from '../assets/reading-towers/staged/staged-living-kitchen-1400.png';
import stagedKitchenAlt from '../assets/reading-towers/staged/staged-kitchen-alt-1400.png';
import x17Bedroom from '../assets/reading-towers/units/reference/rt-x17/x17-bedroom-1536.jpg';
import x17Living from '../assets/reading-towers/units/reference/rt-x17/x17-living-1536.jpg';

// Swap the Residences page photos by changing these direct imports.
const residenceHero = {
  src: stagedLivingKitchen,
  srcSet: `${stagedLivingKitchen} 1x`,
  alt: 'Reading Tower staged living room and kitchen',
};

const residenceFeature = {
  src: x17Living,
  srcSet: `${x17Living} 1536w`,
  alt: 'Reading Tower living room',
};

const residenceKitchen = {
  src: stagedKitchenAlt,
  srcSet: `${stagedKitchenAlt} 1x`,
  alt: 'Reading Tower staged kitchen',
};

const residenceBedroom = {
  src: x17Bedroom,
  srcSet: `${x17Bedroom} 1536w`,
  alt: 'Reading Tower bedroom',
};

const ResidencesPage = () => {
  return (
    <div className="bg-white text-gray-700">
      {/* Section 1: Main Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-sm font-semibold uppercase tracking-wider text-teal-700">
              RESIDENCES
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-1400 leading-tight">
              Rooted in Downtown, Reimagined for Comfortable Living
            </h1>
            <p className="text-lg leading-relaxed text-gray-600">
              Reading Tower offers a collection of studio, one-bedroom, and two-bedroom residences within a prominent high-rise located in the heart of Downtown Reading. The building has been reimagined into contemporary apartments with expansive windows, open layouts, and city views from multiple sides.
            </p>
            <p className="text-lg leading-relaxed text-gray-600">
              With solid construction and modern interior upgrades, Reading Tower blends strong architectural structure with today's finishes and conveniences. Thoughtful layouts, generous storage, and abundant natural light create comfortable living spaces designed for both relaxation and productivity.
            </p>
            <p className="text-lg leading-relaxed text-gray-600">
              Located at 601 Penn Street, Reading Tower places residents in a central downtown location near restaurants, local businesses, and public transportation options.
            </p>
            <p className="text-lg leading-relaxed text-gray-600 font-medium">
              Modern. Spacious. Centrally located. Welcome to Reading Tower.
            </p>
          </div>
          <div>
            <img
              src={residenceHero.src}
              srcSet={residenceHero.srcSet}
              sizes="(min-width: 768px) 50vw, 100vw"
              alt={residenceHero.alt}
              className="rounded-lg shadow-xl w-full aspect-[4/3] object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Section 2: Full-Width Feature Image */}
      <section className="py-8 md:py-12 overflow-visible">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src={residenceFeature.src}
            srcSet={residenceFeature.srcSet}
            sizes="100vw"
            alt={residenceFeature.alt}
            className="rounded-xl shadow-2xl w-full aspect-[16/9] object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      {/* Section 3: General Apartment Features List */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-semibold text-center text-gray-1400 mb-12">
            Inside Your Reading Tower Home
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl mx-auto text-gray-600">
            <ul className="space-y-3 list-disc list-inside">
              <li>Spacious studio, one-bedroom, and two-bedroom residences*</li>
              <li>Large windows with abundant natural light and city views</li>
              <li>Open, efficient floor plans designed for modern living</li>
              <li>Washer and dryers on every floor</li>
            </ul>
            <ul className="space-y-3 list-disc list-inside">
              <li>LED lighting throughout</li>
              <li>Generous closet space</li>
              <li>Pre-wired for high-speed internet</li>
            </ul>
          </div>
          <p className="text-center text-xs text-gray-500 mt-8">*Floor plan types and specific features may vary. Please inquire for details.</p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            <div className="space-y-8">
              <img
                src={residenceKitchen.src}
                srcSet={residenceKitchen.srcSet}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt={residenceKitchen.alt}
                className="rounded-lg shadow-xl w-full aspect-[4/3] object-cover"
                loading="lazy"
                decoding="async"
              />
              <div>
                <h3 className="text-2xl font-semibold text-teal-700 mb-5">
                  Kitchen Highlights
                </h3>
                <ul className="space-y-3 text-gray-600 list-disc list-inside">
                  <li>Open layouts ideal for everyday living and entertaining</li>
                  <li>Contemporary kitchen cabinetry</li>
                  <li>Stainless steel appliances</li>
                  <li>Durable quartz countertops</li>
                  <li>Full-height tile backsplash</li>
                </ul>
              </div>
            </div>

            <div className="space-y-8">
              <img
                src={residenceBedroom.src}
                srcSet={residenceBedroom.srcSet}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt={residenceBedroom.alt}
                className="rounded-lg shadow-xl w-full aspect-[4/3] object-cover"
                loading="lazy"
                decoding="async"
                />
                <div>
                <h3 className="text-2xl font-semibold text-teal-700 mb-5">
                  Residence Features
                </h3>
                <ul className="space-y-3 text-gray-600 list-disc list-inside">
                  <li>Large windows</li>
                  <li>City views</li>
                  <li>Open layouts</li>
                  <li>Modern interior</li>
                  <li>Generous storage</li>
                </ul>
                </div>
              </div>
              </div>
            </div>
            </section>
    </div>
  );
};

export default ResidencesPage;
